import styled from "@emotion/styled";
import { Button, Modal, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FC, Fragment, ReactNode, useState } from "react";
import { CreditTransaction } from "services/factoryCreditApi";
import { formatDate, formatDateWithTime } from "utils/dateFormat";
import { numberFormatInt } from "utils/nuberFormat";
import { CREDIT_NEGATIVE_COLOR, CREDIT_POSITIVE_COLOR } from "utils/creditDisplay";
import { CREDIT_DEBT_LABEL_SUFFIX, isCodFailOrderReleaseRow, isCodReserveDebtRow } from "utils/creditTransactionDisplay";
import { systemTypeLabel } from "./creditTransactionOptions";

interface CreditTransactionTableProps {
  data: CreditTransaction[];
  loading: boolean;
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number, pageSize: number) => void;
}

const formatLedgerAmount = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(Number(n)) || Number(n) === 0) return "";
  return String(numberFormatInt(Math.round(Math.abs(Number(n)))));
};

const formatRemaining = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const rounded = Math.round(Number(n));
  if (rounded < 0) return `-${numberFormatInt(Math.abs(rounded))}`;
  return String(numberFormatInt(rounded));
};

/** รับ/จ่าย/คงเหลือ — fallback ยอด signed + โควต้าหลัง ถ้า API ยังไม่ส่งฟิลด์ใหม่ */
const ledgerAmounts = (row: CreditTransaction) => {
  const hasLedger =
    row.debit_display != null ||
    row.credit_display != null ||
    row.remaining_after_display != null;
  if (hasLedger) {
    return {
      debit: Number(row.debit_display ?? 0),
      credit: Number(row.credit_display ?? 0),
      remaining: row.remaining_after_display ?? null,
    };
  }
  const signed = Number(row.amount_display ?? 0);
  return {
    debit: signed < 0 ? Math.abs(signed) : 0,
    credit: signed > 0 ? signed : 0,
    remaining: row.quota_after ?? null,
  };
};

/** ต่อท้าย (เครดิตค้างชำระ) เป็นสีแดง — ส่วนข้อความหลักสีปกติ */
const renderDisplayLabel = (label: string | null | undefined): ReactNode => {
  const text = label || "—";
  const idx = text.indexOf(CREDIT_DEBT_LABEL_SUFFIX);
  if (idx < 0) return <Typography>{text}</Typography>;
  return (
    <Typography>
      {text.slice(0, idx)}
      <span style={{ color: CREDIT_NEGATIVE_COLOR }}>{CREDIT_DEBT_LABEL_SUFFIX}</span>
    </Typography>
  );
};

/**
 * 2026-09-07: แถว TOPUP — โชว์รายละเอียดการสั่งซื้อในคอลัมน์ "รายการ" แทนที่จะโชว์แค่ "เติมเครดิต" เฉยๆ
 * (ตามที่ขอ): เติมเครดิต / Package X Credits / หมดอายุภายใน X วัน / ราคา X บาท / ผู้ทำรายการ
 * ใช้ credit_display (คำนวณ × multiplier ที่ถูกต้องมาจาก backend แล้ว) แทนการคูณ multiplier เองที่นี่
 * 2026-09-07: จัดรายละเอียดเป็น 2 คอลัมน์ (ป้ายชื่อ/ค่า) แนวกริดให้เป็นระเบียบ แทนบรรทัดเดี่ยวเรียงกัน
 */
const renderTopupDetail = (row: CreditTransaction): ReactNode => {
  const creditAmount = row.credit_display ?? row.amount_display ?? 0;
  const detailRows: Array<[string, string]> = [
    ["Package", `${numberFormatInt(Math.round(Math.abs(creditAmount)))} Credits`],
  ];
  if (row.package_expiry_days != null) {
    const expiresAtText = row.lot_expires_at ? ` (${formatDate(row.lot_expires_at).trim()})` : "";
    detailRows.push(["หมดอายุภายใน", `${numberFormatInt(row.package_expiry_days)} วัน${expiresAtText}`]);
  }
  if (row.paid_amount != null) {
    detailRows.push([
      "ราคา",
      `${Number(row.paid_amount).toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท`,
    ]);
  }
  if (row.topup_person_name && row.topup_person_name !== "-") {
    detailRows.push(["ผู้ทำรายการ", row.topup_person_name]);
  }

  return (
    <div style={{ padding: "4px 0" }}>
      <Typography style={{ marginBottom: 6 }}>{row.display_label_th || "เติมเครดิต"}</Typography>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 8, rowGap: 3 }}>
        {detailRows.map(([label, value]) => (
          <Fragment key={label}>
            <Typography style={{ fontSize: 12, color: "#8c8c8c" }}>{label}</Typography>
            <Typography style={{ fontSize: 12, color: "#8c8c8c" }}>{value}</Typography>
          </Fragment>
        ))}
      </div>
    </div>
  );
};

/**
 * 2026-09-07: mock เลขที่ใบกำกับภาษีอย่างย่อ (ABB) — เอาไว้ทดสอบ UX ปุ่ม "ใบกำกับภาษีอย่างย่อ (PDF)"
 * ก่อนที่ระบบออก e-tax จริงสำหรับการเติมเครดิตจะถูกสร้างขึ้น (คุยกันไว้ พักเรื่อง e-tax จริงไว้ก่อน)
 *
 * รูปแบบอ้างอิงจากของจริงที่ระบบใช้อยู่ทุกวันนี้กับ ABB ของออเดอร์ (ดู EtaxSequenceService.format ฝั่ง
 * API): `ABB-YYYYMM-NNNNNN` — "ABB" ไม่ได้ hardcode ลอยๆ แต่เป็นชื่อ doc_type คงที่ของระบบ e-tax ทั้งระบบ
 * (EtaxDocType.ABB) ส่วนเลขวิ่ง 6 หลักท้ายของจริงมาจาก etax_sequence_counter (นับต่อ doc_type+เดือน)
 * ซึ่งยังไม่มีการสร้างจริงสำหรับ TOPUP เลย — mock นี้จึงเอาเลขในเลขที่ทำรายการ (ref_display_no) ท้าย 6
 * หลักมาแทนเฉยๆ (ตัวเลขไม่ตรงกับที่จะเกิดขึ้นจริง แค่ให้หน้าตารูปแบบถูกต้อง) เดือนใช้ create_date จริงของ
 * แถวนั้น (ของจริงก็ผูกกับเดือนที่ออกเอกสารเหมือนกัน) — ไม่มีการเรียก API/สร้างไฟล์ PDF ใดๆ ทั้งสิ้น
 */
/**
 * 2026-09-09: ตามที่ขอ — ซ่อนปุ่ม "ใบกำกับภาษีอย่างย่อ (PDF)" ไปก่อน (พักงาน e-tax จริงไว้ให้ทีมงานทำต่อ
 * ดู memory project_etax_credit_topup_abb_design.md) ไม่ลบโค้ด mock ทิ้ง เผื่อกลับมาใช้/ทำต่อทีหลัง —
 * สลับกลับมาแสดงได้แค่ตั้งเป็น true
 */
const SHOW_ABB_PREVIEW_BUTTON = false;

const buildMockAbbCode = (row: CreditTransaction): string => {
  const d = row.create_date ? new Date(row.create_date) : new Date();
  const period = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
  const digits = (row.ref_display_no ?? row.order_code ?? "").replace(/\D/g, "");
  const seq = (digits.slice(-6) || "000000").padStart(6, "0");
  return `ABB-${period}-${seq}`;
};

const CreditTransactionTable: FC<CreditTransactionTableProps> = ({
  data,
  loading,
  total,
  page,
  limit,
  onPageChange,
}) => {
  // 2026-09-07: preview modal ของปุ่ม "ใบกำกับภาษีอย่างย่อ (PDF)" — mock เฉยๆ ยังไม่มีเอกสารจริง
  const [abbPreviewRow, setAbbPreviewRow] = useState<CreditTransaction | null>(null);

  const columns: ColumnsType<CreditTransaction> = [
    {
      title: "วันเวลา",
      dataIndex: "create_date",
      key: "create_date",
      width: 150,
      render: (v: string) => <Typography>{v ? formatDateWithTime(v) : "—"}</Typography>,
    },
    {
      title: "รายการ",
      dataIndex: "display_label_th",
      key: "display_label_th",
      width: 320,
      render: (v: string, row) =>
        String(row.type).toUpperCase() === "TOPUP" ? renderTopupDetail(row) : renderDisplayLabel(v),
    },
    {
      title: "เลขที่ทำรายการ",
      dataIndex: "ref_display_no",
      key: "ref_display_no",
      width: 160,
      render: (v: string | null, row) => (
        <div>
          <Typography>{v || "—"}</Typography>
          {SHOW_ABB_PREVIEW_BUTTON && String(row.type).toUpperCase() === "TOPUP" && (
            <Button
              type="primary"
              size="small"
              style={{ marginTop: 4, fontSize: 12 }}
              onClick={() => setAbbPreviewRow(row)}
            >
              ใบกำกับภาษีอย่างย่อ (PDF)
            </Button>
          )}
        </div>
      ),
    },
    {
      title: "เลขพัสดุ",
      dataIndex: "tracking_number",
      key: "tracking_number",
      width: 150,
      render: (v: string | null) => <Typography>{v || "—"}</Typography>,
    },
    {
      title: "ระบบ",
      dataIndex: "system_type",
      key: "system_type",
      width: 140,
      render: (v: string | null) => <Typography>{systemTypeLabel(v)}</Typography>,
    },
    {
      // รวม "รับ"/"หัก" เดิมเป็นคอลัมน์เดียว — แต่ละแถวมีค่าแค่ฝั่งเดียวอยู่แล้ว (credit หรือ debit
      // อย่างใดอย่างหนึ่ง ไม่มีแถวไหนมีทั้งสองพร้อมกัน) จึงไม่เสียข้อมูล
      title: "จำนวน",
      key: "amount_display",
      width: 110,
      align: "right",
      render: (_: unknown, row) => {
        const { credit, debit } = ledgerAmounts(row);
        if (credit > 0) {
          const text = formatLedgerAmount(credit);
          if (!text) return "";
          return (
            <Typography style={{ fontWeight: 700, color: CREDIT_POSITIVE_COLOR }}>
              {`+${text}`}
            </Typography>
          );
        }
        if (debit > 0) {
          const text = formatLedgerAmount(debit);
          if (!text) return "";
          return (
            <Typography style={{ fontWeight: 700, color: CREDIT_NEGATIVE_COLOR }}>
              {`-${text}`}
            </Typography>
          );
        }
        return "";
      },
    },
    {
      title: "คงเหลือ",
      key: "remaining_after_display",
      width: 120,
      align: "right",
      render: (_: unknown, row) => {
        const { remaining } = ledgerAmounts(row);
        const text = formatRemaining(remaining);
        const isNeg = remaining != null && Number(remaining) < 0;
        return (
          <Typography
            style={{
              fontWeight: 600,
              color: isNeg ? CREDIT_NEGATIVE_COLOR : undefined,
            }}
          >
            {text}
          </Typography>
        );
      },
    },
  ];

  return (
    <TableWrap>
      <Table<CreditTransaction>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={data.filter(
          (row) => !isCodFailOrderReleaseRow(row) && !isCodReserveDebtRow(row) && !row.is_grouped_duplicate
        )}
        loading={loading}
        scroll={{ x: true }}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["20", "50", "100"],
          onChange: onPageChange,
        }}
      />

      {/* 2026-09-07: mock ยังไม่มีเอกสาร/ไฟล์ PDF จริง — โชว์แค่เลขที่เอกสารตัวอย่างให้ดูรูปแบบก่อน
       * (ของจริงในระบบจะ redirect ไป Azure Blob SAS URL ชั่วคราว ไม่มี PDF ผ่านเซิร์ฟเวอร์เราตรงๆ) */}
      <Modal
        open={!!abbPreviewRow}
        onCancel={() => setAbbPreviewRow(null)}
        footer={null}
        title="ใบกำกับภาษีอย่างย่อ (ตัวอย่าง)"
        centered
        width={400}
      >
        {abbPreviewRow && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Typography style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
              เลขที่เอกสาร (ตัวอย่าง — ยังไม่ใช่เอกสารจริง)
            </Typography>
            <Typography style={{ fontSize: 22, fontWeight: 700, letterSpacing: 1 }}>
              {buildMockAbbCode(abbPreviewRow)}
            </Typography>
            <Typography style={{ fontSize: 12, color: "#8c8c8c", display: "block", marginTop: 16 }}>
              เอกสาร PDF ใบกำกับภาษีอย่างย่อจริงจะแสดงที่นี่ เมื่อระบบออกใบกำกับภาษีสำหรับการเติมเครดิตพร้อมใช้งาน
            </Typography>
          </div>
        )}
      </Modal>
    </TableWrap>
  );
};

export default CreditTransactionTable;

/**
 * ให้เท่าตารางเมนูคำสั่งซื้อ (OrderTableList):
 * - หัวตาราง 12px ตาม main.css ของระบบ
 * - เนื้อหาเซลล์ 14px ผ่าน Typography เหมือนคอลัมน์คำสั่งซื้อ
 */
const TableWrap = styled.div`
  .ant-table-thead > tr > th {
    font-size: 12px !important;
    font-weight: 700;
  }

  .ant-table,
  .ant-table-tbody > tr > td,
  .ant-table-tbody > tr > td .ant-typography {
    font-size: 14px !important;
  }
`;
